/**
 * Browser session for the mobile client app.
 *
 * packages/shared/gotrue.ts knows how to talk to GoTrue but deliberately holds
 * no state, because the two React Native apps persist sessions differently.
 * This file is the browser half: storage, refresh-ahead, and a single-flight
 * refresh so a burst of parallel queries on a cold page cannot mint several
 * sessions and invalidate each other's refresh token.
 *
 * The storage key is app-specific on purpose. localStorage is already
 * per-origin, but distinct keys mean that even a shared-origin deployment
 * could never let one app pick up the other's session.
 */
import {
  requestOtp, verifyOtp, refreshSession, signOut as gotrueSignOut,
  needsRefresh, googleSignInUrl, type Session,
} from '@transport-booking/shared'

const AUTH_URL = (import.meta.env.VITE_AUTH_URL as string | undefined) ?? '/auth/v1'
const KEY = 'tb.mobile.session'
const LEGACY_KEY = 'tb.mobile.token'

/** undefined = not read from storage yet; null = definitely signed out. */
let cache: Session | null | undefined
let inflight: Promise<string | null> | null = null

function write(s: Session | null): void {
  cache = s
  try {
    if (s) localStorage.setItem(KEY, JSON.stringify(s))
    else { localStorage.removeItem(KEY); localStorage.removeItem(LEGACY_KEY) }
  } catch { /* private window: the session just will not survive a reload */ }
}

function read(): Session | null {
  if (cache !== undefined) return cache
  try {
    const raw = localStorage.getItem(KEY)
    if (raw) { cache = JSON.parse(raw) as Session; return cache }

    // A token pasted into the old development sign-in keeps working: adopt it
    // as a session that can never be refreshed. Without this, everyone with an
    // open tab is signed out the moment this build ships.
    const legacy = localStorage.getItem(LEGACY_KEY)
    if (legacy) {
      write({
        access_token: legacy,
        refresh_token: '',
        expires_at: Number.MAX_SAFE_INTEGER,
        user: { id: '', email: null, created_at: '' },
      })
      return cache ?? null
    }
  } catch { /* blocked site data */ }
  cache = null
  return cache
}

/**
 * Subscribers told when a session ends WITHOUT anyone signing out -- an
 * expired refresh token, a revoked session, a legacy token that can never be
 * renewed.
 *
 * hasSession() is a plain storage read, so React has no way to notice that
 * the session it rendered with has just been thrown away. Without this the
 * app keeps rendering its signed-in shell around queries that can only 401,
 * which is how one tab came to send 281 /me in 27 seconds.
 */
type SessionLostHandler = () => void
const sessionLost = new Set<SessionLostHandler>()

/** Subscribe; returns the unsubscribe, so this is a useSyncExternalStore. */
export function onSessionLost(fn: SessionLostHandler): () => void {
  sessionLost.add(fn)
  return () => { sessionLost.delete(fn) }
}

/** End a session nobody chose to end -- once, and tell the app. */
export function loseSession(): void {
  if (read() === null) return          // already gone; do not notify twice
  write(null)
  for (const fn of [...sessionLost]) fn()
}

export function hasSession(): boolean { return read() !== null }
export function currentEmail(): string | null { return read()?.user.email ?? null }
export function getAccessToken(): string | null { return read()?.access_token ?? null }

/**
 * The access token to send, refreshed first if it is close to expiring.
 * Single-flight: parallel queries share one refresh.
 */
export async function ensureFreshToken(): Promise<string | null> {
  const s = read()
  if (!s) return null
  if (!s.refresh_token) return s.access_token          // legacy development token
  if (!needsRefresh(s)) return s.access_token
  return forceRefresh()
}

/** Refresh unconditionally — for a 401 the proactive check did not predict. */
export function forceRefresh(): Promise<string | null> {
  if (inflight) return inflight
  const s = read()
  // A legacy development token carries no refresh token and can never be
  // renewed. Returning null while LEAVING it in storage left the sign-in page
  // reporting INVALID_TOKEN on every load until someone cleared site data by
  // hand -- and left hasSession() true, so nothing ever moved on.
  if (!s?.refresh_token) { loseSession(); return Promise.resolve(null) }

  inflight = refreshSession(AUTH_URL, s.refresh_token)
    .then((next) => { write(next); return next.access_token })
    .catch(() => { loseSession(); return null })       // revoked or expired
    .finally(() => { inflight = null })
  return inflight
}

export async function startOtp(email: string): Promise<void> {
  await requestOtp(AUTH_URL, email)
}

export async function completeOtp(email: string, code: string): Promise<Session> {
  const s = await verifyOtp(AUTH_URL, email, code)
  write(s)
  return s
}

export async function endSession(): Promise<void> {
  const s = read()
  // Clear THIS device first, and never let the server call stand in the way.
  //
  // It used to await gotrueSignOut() before write(null), so any failure --
  // a token GoTrue had already expired, a dead network, a 5xx -- threw past
  // the write and left the session intact. The Sign out button then did
  // nothing at all, with no error shown, which is how it was found.
  //
  // Revoking the refresh token server-side is worth attempting and is not
  // worth trapping someone in a signed-in app for.
  write(null)
  if (s?.access_token && s.refresh_token) {
    try {
      await gotrueSignOut(AUTH_URL, s.access_token)
    } catch {
      /* best effort: the local session is already gone */
    }
  }
}

export function googleUrl(): string {
  return googleSignInUrl(AUTH_URL, location.origin)
}

/**
 * Adopt a session handed back in the URL fragment by an OAuth redirect.
 *
 * GoTrue returns the tokens in the hash rather than the query string, so they
 * never reach the server or a proxy log. They are stripped from the address
 * bar immediately so a copied URL cannot leak a live session.
 */
export function adoptFromUrlHash(): boolean {
  if (location.hash.length < 2) return false
  const p = new URLSearchParams(location.hash.slice(1))
  const access = p.get('access_token')
  const refresh = p.get('refresh_token')
  if (!access || !refresh) return false

  write({
    access_token: access,
    refresh_token: refresh,
    expires_at: Math.floor(Date.now() / 1000) + Number(p.get('expires_in') ?? 3600),
    user: { id: '', email: null, created_at: '' },
  })
  history.replaceState(null, '', location.pathname + location.search)
  return true
}
